import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase, ensureEntityFolder, getClient, updateClient, getActivityLog, getTasks, addActivityLog, getDocuments, addDocument, getProfiles, createTask, updateTask, getAuthHistory, fullNameLastFirst } from '../lib/supabase';
import { CLIENT_DRIVE_ROOT, CLIENT_DOC_BUCKETS, fileToBase64 } from '../lib/driveRoots';
import AuthHistoryTable from '../components/AuthHistoryTable';
import { loadApprovedCounties, serviceAreaStatus } from '../lib/serviceArea';
import ProfileTabBar from '../components/ProfileTabBar';
import { useAuth } from '../hooks/useAuth';
import useRecordNav from '../hooks/useRecordNav';
import RecordNav from '../components/RecordNav';
import StageTransitionModal from '../components/StageTransitionModal';
import { listClientFolder, flattenBucket } from '../lib/driveListing';
import { ClientVisitsView } from '../components/clientTabs/ClientVisitsTabs';
import { ClientAssessments, ClientCarePlan } from '../components/clientTabs/ClientAssessmentsTabs';
import MarPanel from '../components/clinical/MarPanel';
import ActivityNote, { canEditNotes } from '../components/ActivityNote';
import InlineNote from '../components/InlineNote';
import CaregiverReports from '../components/clinical/CaregiverReports';
import ClientScheduleTab from '../components/clientTabs/ClientScheduleTab';
import ClientVerificationTab from '../components/clientTabs/ClientVerificationTab';
import ClientQmTab from '../components/clientTabs/ClientQmTab';
import ClientDmeTab from '../components/clientTabs/ClientDmeTab';
import { useModules } from '../components/RoleGuard';
import ClientSupervisorVisitsTab from '../components/clientTabs/ClientSupervisorVisitsTab';
import PhysicianRequestModal from '../components/clientTabs/PhysicianRequestModal';
import ClientDocumentsTab from '../components/clientTabs/ClientDocumentsTab';
import ClientSubmissions from '../components/clientTabs/ClientSubmissions';
import ClientCareTeam from '../components/clientTabs/ClientCareTeam';
import ClientESignatures from '../components/clientTabs/ClientESignatures';
import ClientFaxesTab from '../components/clientTabs/ClientFaxesTab';
import { getFaxDraft } from '../lib/faxDrafts';
import ClientDocReviewTab from '../components/clientTabs/ClientDocReviewTab';
import { resolveAssessmentPath } from '../lib/documents/packets';
import { isDmeOnly, hasDme, isPrivateTrack, isGapp, payerLabel, isClosedClient, isReceivingService } from '../lib/serviceModel';
import CoverageCard from '../components/clientTabs/CoverageCard';
import { format, differenceInYears, differenceInMonths, differenceInDays } from 'date-fns';
import { parseLocalDate } from '../lib/dateUtils';
import { pushClientToAxisCare } from '../lib/axiscareClient';
import { petSummary, hasPets } from '../lib/pets';
import ClientTrail, { tabCrumbs } from '../components/ClientTrail';
import { rememberClient } from '../lib/recentClients';

const zoneBadge = (status) => {
  const map = { 'New Referral':'badge-new','Client Contacted':'badge-new','Response Pending':'badge-pending','Waitlist':'badge-pending','Intake Form Submitted':'badge-processing','Nursing Assessment Pending':'badge-processing','Nursing Assessment Completed':'badge-processing','PPOT Request Pending':'badge-processing','PPOT Request In Progress':'badge-processing','Submitted (DMA6A)':'badge-approval','Submitted (DMA80)':'badge-approval','Approved by GAPP':'badge-approval','Active (Receiving Service)':'badge-active','On-Hold/Suspended':'badge-closed','Service Suspended':'badge-closed','Discharged':'badge-closed','GAPP Application Denied':'badge-closed','Duplicate':'badge-closed','Lost':'badge-closed' };
  return map[status] || 'badge-new';
};

// Folders a file can be uploaded into: the standard set, whatever else exists
// in the client's Drive folder, and each PA cycle's folder under PA Submissions
// (a transfer's signed Appendix R belongs with that submission, not in the
// old Signing Packets folder).
function uploadTargets(driveTree) {
  const out = [...CLIENT_DOC_BUCKETS];
  (driveTree?.folders || []).forEach((f) => {
    if (!out.includes(f.name)) out.push(f.name);
    if (f.name === 'PA Submissions') (f.subfolders || []).forEach((sf) => out.push(`PA Submissions/${sf.name}`));
  });
  return out;
}

const Detail = ({ label, value }) => (
  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'8px 0', borderBottom:'1px solid var(--border)', gap:12 }}>
    <span style={{ fontSize:12, color:'var(--text3)', flexShrink:0, width:150 }}>{label}</span>
    <span style={{ fontSize:13, color:'var(--text)', fontWeight:500, textAlign:'right' }}>{value || '—'}</span>
  </div>
);

const Section = ({ title, children }) => (
  <div className="card" style={{ marginBottom:12, border:'0.5px solid var(--accent-border)' }}>
    <div style={{ padding:'10px 16px', borderBottom:'0.5px solid var(--accent-border)', background:'var(--accent-bg)' }}>
      <div style={{ fontSize:11, fontWeight:600, color:'var(--accent)', textTransform:'uppercase', letterSpacing:'0.07em' }}>{title}</div>
    </div>
    <div style={{ padding:'0 16px' }}>{children}</div>
  </div>
);

const dateVal = (dateStr) => {
  if (!dateStr) return null;
  const days = differenceInDays(parseLocalDate(dateStr), new Date());
  const formatted = format(parseLocalDate(dateStr), 'MMM d, yyyy');
  if (days <= 60) return <span style={{ color:'var(--danger)', fontWeight:600 }}>{formatted} ⚠</span>;
  return formatted;
};

const GridCell = ({ label, value, span, mono }) => (
  <div style={{ padding:'10px 14px', borderRight:'0.5px solid var(--border)', borderBottom:'0.5px solid var(--border)', gridColumn: span ? `span ${span}` : undefined }}>
    <div style={{ fontSize:12, color:'var(--text3)', marginBottom:3 }}>{label}</div>
    <div style={{ fontSize:13, fontWeight:500, color:'var(--text)', fontFamily: mono ? 'monospace' : undefined }}>{value || '—'}</div>
  </div>
);

const FlagChip = ({ label, value }) => (
  <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, padding:'6px 10px', border:'0.5px solid var(--border)', borderRadius:8 }}>
    <div style={{ width:7, height:7, borderRadius:'50%', flexShrink:0, background: value === true ? 'var(--danger)' : value === false ? 'var(--success)' : 'var(--text3)' }} />
    <span style={{ color:'var(--text2)', fontWeight:500 }}>{label}</span>
  </div>
);

const Tag = ({ children }) => (
  <span style={{ display:'inline-block', fontSize:11, fontWeight:500, padding:'3px 9px', borderRadius:6, margin:'2px', background:'var(--accent-bg)', color:'var(--accent)' }}>{children}</span>
);

export default function ClientProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { getAccess } = useModules();

  // Prev / next client — follows the Clients list order when arriving from it, alphabetical otherwise
  const recordNav = useRecordNav({ table: 'clients', id, base: '/clients', stateKey: 'clientIds' });
  const [client, setClient] = useState(null);
  // The approved area (Settings → Approved Counties); null until loaded.
  const [approvedList, setApprovedList] = useState(null);
  useEffect(() => { loadApprovedCounties().then(({ data, error }) => setApprovedList(error ? null : data)); }, []);
  const [activityLog, setActivityLog] = useState([]);
  const [tasks, setTasks] = useState([]);
  // Tabs live in the URL so a client's MAR or care plan can be linked to.
  // The keys the profile used before grouping still resolve, so old links work.
  const [searchParams, setSearchParams] = useSearchParams();
  const LEGACY_TAB = { visits: ['calendar', null], assessments: ['clinical', 'assessments'], careplan: ['clinical', 'careplan'], supervisor: ['clinical', 'supervision'], mar: ['clinical', 'mar'], complaints: ['qm', 'complaints'], faxes: ['documents', 'faxes'] };
  const rawTab = searchParams.get('tab') || 'overview';
  const [activeTab, subFromLegacy] = LEGACY_TAB[rawTab] || [rawTab, null];
  const DEFAULT_SUB = { clinical: 'summary', qm: 'surveys', documents: 'files' };
  const sub = subFromLegacy || searchParams.get('sub') || DEFAULT_SUB[activeTab] || 'summary';
  const setTabs = (tab, subKey) => setSearchParams(prev => {
    const n = new URLSearchParams(prev);
    tab === 'overview' ? n.delete('tab') : n.set('tab', tab);
    (!subKey || subKey === DEFAULT_SUB[tab]) ? n.delete('sub') : n.set('sub', subKey);
    return n;
  }, { replace: true });
  const setActiveTab = (tab) => setTabs(tab, null);
  const [showStageModal, setShowStageModal] = useState(false);
  // A prefill from elsewhere — recording a DMA-80 submission opens this with the
  // stage and the note already written.
  const [stagePrefill, setStagePrefill] = useState({ status: '', notes: '', familyEmailHandled: false });
  const [showLogModal, setShowLogModal] = useState(false);
  const [logNote, setLogNote] = useState('');
  const [logType, setLogType] = useState('Care Coordination');
  const [loading, setLoading] = useState(true);
  // Supervision due-state for the banner, from the same supervision_status view
  // the Supervision tab reads — so the two can never disagree.
  const [supervision, setSupervision] = useState([]);
  const [documents, setDocuments] = useState([]);
  // Live listing of the client's Drive folder — the source of truth for what is on file.
  const [driveTree, setDriveTree] = useState(null);
  const [driveLoading, setDriveLoading] = useState(false);
  const [driveErr, setDriveErr] = useState('');
  // Also the safety net for folder creation: every client is meant to get a
  // Drive folder the moment their G-number exists, but that call is
  // fire-and-forget at creation and has failed before (a transient 500, or a
  // write-back that never ran). So reading the folder here creates it if it
  // is missing and records the id if the client row lost it.
  //
  // When the row already carries a folder id the function reads that folder
  // directly and never searches by name or creates. This page loads for every client, so the
  // old behaviour raced the fire-and-forget create on a client seconds old —
  // both searched, both found nothing, both created, and G113/G114 each ended
  // up with two folders on 2026-09-22. The net only casts when there is
  // genuinely no id to go on.
  const loadDrive = useCallback(async (code, uuid, knownFolderId) => {
    if (!code) return;
    setDriveLoading(true); setDriveErr('');
    try {
      let tree = await listClientFolder(code);
      if (!tree.folderId) {
        const made = await ensureEntityFolder('client', code);
        if (made) tree = await listClientFolder(code);
      }
      setDriveTree(tree);
      if (tree.folderId && uuid && tree.folderId !== knownFolderId) {
        await updateClient(uuid, { drive_folder_id: tree.folderId });
        setClient(c => (c ? { ...c, drive_folder_id: tree.folderId } : c));
      }
    }
    catch (e) { setDriveErr(e.message || 'Could not read the Drive folder'); }
    finally { setDriveLoading(false); }
  }, []);
  const [latestAssessment, setLatestAssessment] = useState(null);
  const [authHistory, setAuthHistory] = useState(null);
  const [showDocModal, setShowDocModal] = useState(false);
  // When the upload is started from inside a folder, that folder is fixed.
  const [uploadLocked, setUploadLocked] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestDraft, setRequestDraft] = useState(null);   // a saved Request documents draft being resumed
  const [faxRefresh, setFaxRefresh] = useState(0);
  const openRequest = (draft = null) => { setRequestDraft(draft || null); setShowRequestModal(true); };
  // The Fax Manager resumes a Request documents draft by sending you here with
  // ?draft=<id>: open it once the client is loaded, then drop the parameter.
  const draftParam = searchParams.get('draft');
  useEffect(() => {
    if (!draftParam || !client?.id) return;
    let live = true;
    getFaxDraft(draftParam).then(({ data }) => {
      if (!live) return;
      if (data && data.kind === 'request' && data.client_id === client.id) openRequest(data);
      setSearchParams(prev => { const n = new URLSearchParams(prev); n.delete('draft'); return n; }, { replace: true });
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftParam, client?.id]);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskForm, setTaskForm] = useState({ title:'', assigned_to_id:'', priority:'Normal', due_date:'' });
  const [taskError, setTaskError] = useState('');
  const [axisCareBusy, setAxisCareBusy] = useState(false);
  const [axisCareMsg, setAxisCareMsg] = useState('');
  const [taskSaving, setTaskSaving] = useState(false);
  const [taskStaff, setTaskStaff] = useState([]);
  const [uploadForm, setUploadForm] = useState({ file: null, bucket: 'Needs Review', notes: '' });
  const [docSaving, setDocSaving] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // The search box's "Recent clients" — this one goes to the top of the list.
  useEffect(() => { if (client?.id && profile?.id) rememberClient(profile.id, client.id); }, [client?.id, profile?.id]);

  const loadClient = useCallback(async () => {
    const { data } = await getClient(id);
    setClient(data);
    setLoading(false);
    if (data) {
      getActivityLog(id).then(({ data: logs }) => setActivityLog(logs || []));
      getTasks({ clientId: id }).then(({ data: t }) => setTasks(t || []));
      getDocuments(id).then(({ data: d }) => setDocuments(d || []));
      loadDrive(data.client_id, data.id, data.drive_folder_id);
      getProfiles().then(({ data: s }) => setTaskStaff(s || []));
      supabase.from('nurse_assessments')
        .select('drive_document_url, updated_at')
        .eq('client_uuid', id)
        .not('drive_document_url', 'is', null)
        .order('updated_at', { ascending: false })
        .limit(1)
        .then(({ data: na }) => setLatestAssessment(na?.[0] || null));
      getAuthHistory(data.id).then(({ data: h }) => setAuthHistory(h || []));
      // What supervision is due, from supervision_status — the view that holds
      // the cadence rules and that the Supervision tab already reads. The
      // banner used to read next_visit_due off whichever supervisor_visits row
      // was created most recently: a column Supervision v2 never fills, picked
      // by creation date rather than visit date, so after the history backfill
      // it showed a green tick with no date beside it — while G025's aide
      // supervision was in fact overdue.
      supabase.from('supervision_status')
        .select('kind, status, next_due, last_date, staff_full, position_code')
        .eq('client_id', id)
        .then(({ data, error }) => { if (!error) setSupervision(data || []); });
    }
  }, [id, loadDrive]);

  // Eligibility as a history, drawn by CoverageCard. The columns on `clients`
  // are the current value; these are every change ever recorded, including the
  // 85 backfilled from pasted GAMMIS portal results. An unchanged answer
  // confirms the newest row rather than adding one.
  const [eligHistory, setEligHistory] = useState([]);
  useEffect(() => {
    if (!id) return;
    supabase.from('eligibility_checks')
      .select('id, checked_at, last_confirmed_at, confirmations, result, status_text, coverage_start, coverage_end, coverage_end_is_plan_end, insurance_type, aid_category, managed_care_org, reject_reason, change_summary, channel, trigger, checked_by_name')
      .eq('client_id', id).order('checked_at', { ascending: false })
      .then(({ data }) => setEligHistory(data || []));
  }, [id]);

  useEffect(() => {
    loadClient();
  }, [loadClient]);

  // Refresh data when user returns to this page (e.g. after editing)
  useEffect(() => {
    const handleFocus = () => loadClient();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [loadClient]);

  const handleLogActivity = async () => {
    if (!logNote.trim()) return;
    await addActivityLog({
      client_id: id, client_ref: client.client_id,
      activity_type: logType, notes: logNote,
      performed_by_name: profile.full_name, performed_by_id: profile.id,
    });
    setLogNote(''); setShowLogModal(false);
    getActivityLog(id).then(({ data: logs }) => setActivityLog(logs || []));
  };

  const handleCreateTask = async () => {
    if (!taskForm.title) return;
    setTaskError('');
    setTaskSaving(true);
    try {
      const assignee = taskStaff.find(s => s.id === taskForm.assigned_to_id);
      const { data, error } = await createTask({
        title: taskForm.title,
        priority: taskForm.priority || 'Normal',
        due_date: taskForm.due_date || null,
        status: 'Open',
        assigned_to_id: taskForm.assigned_to_id || null,
        client_id: id,
        client_name: `${client.first_name} ${client.last_name}`,
        client_ref: client.client_id,
        assigned_to_name: assignee?.full_name || null,
        created_by_id: profile?.id,
        created_by_name: profile?.full_name,
      });
      if (error) {
        console.error('Task creation error:', error);
        setTaskError(`Could not create task: ${error.message}`);
        return;
      }
      if (data) {
        setTasks(prev => [data, ...prev]);
        if (taskForm.assigned_to_id && taskForm.assigned_to_id !== profile?.id && assignee?.email) {
          try {
            const { notifyTaskAssigned } = await import('../lib/notifications');
            notifyTaskAssigned(data, assignee.full_name, assignee.email, profile?.full_name);
          } catch (e) { console.warn('Notification failed:', e); }
        }
      }
      setTaskForm({ title:'', assigned_to_id:'', priority:'Normal', due_date:'' });
      setShowTaskModal(false);
    } catch (e) {
      console.error('Task creation exception:', e);
      setTaskError('An unexpected error occurred. Please try again.');
    } finally {
      setTaskSaving(false);
    }
  };

  const handleAttachFile = async () => {
    if (!uploadForm.file) return;
    setDocSaving(true);
    setUploadError('');
    try {
      const fileBase64 = await fileToBase64(uploadForm.file);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Your sign-in has ended. Sign in again, then attach the file.');
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/upload-to-drive`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          fileBase64,
          filename: uploadForm.file.name,
          clientId: client.client_id,
          rootFolderId: CLIENT_DRIVE_ROOT,
          subfolder: uploadForm.bucket,
          contentType: uploadForm.file.type || 'application/octet-stream',
        }),
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'Upload failed');

      if (!client.drive_folder_id && result.clientFolderId) {
        updateClient(id, { drive_folder_id: result.clientFolderId });
        setClient(c => ({ ...c, drive_folder_id: result.clientFolderId }));
      }

      await addDocument({
        client_id: id,
        client_ref: client.client_id,
        document_name: uploadForm.file.name,
        document_type: uploadForm.bucket,
        drive_url: result.webViewLink,
        notes: uploadForm.notes,
        uploaded_by_name: profile.full_name,
        uploaded_by_id: profile.id,
      });
      setUploadForm({ file: null, bucket: 'Needs Review', notes: '' });
      setUploadLocked(false);
      setShowDocModal(false);
      setFaxRefresh(n => n + 1);
      getDocuments(id).then(({ data: d }) => setDocuments(d || []));
      loadDrive(client.client_id, client.id, client.drive_folder_id);
    } catch (e) {
      setUploadError(e.message || 'Something went wrong uploading this file.');
    } finally {
      setDocSaving(false);
    }
  };

  // ── AxisCare (real API) ──────────────────────────────────────────────────
  // Moving a client to Active creates them in AxisCare automatically
  // (clientStatus.js). This button is the fallback for when that could not
  // finish — AxisCare unreachable, or a same-name record that needed a person
  // to say whether it is the same child — and runs the same code.
  const createClientInAxisCare = async () => {
    setAxisCareBusy(true);
    setAxisCareMsg('');
    try {
      const res = await pushClientToAxisCare(client, {
        profile,
        confirmLink: async (m) => window.confirm(
          `A client named ${m.firstName} ${m.lastName} already exists in AxisCare (ID ${m.id}).\n\nLink this client to that existing AxisCare record instead of creating a new one?`
        ),
      });
      if (res.outcome === 'declined') { setAxisCareMsg('Cancelled — no changes made.'); return; }
      setClient(prev => ({ ...prev, axiscare_client_id: res.axisId }));
      getActivityLog(id).then(({ data: logs }) => setActivityLog(logs || []));
      setAxisCareMsg(res.outcome === 'created'
        ? `✓ Created in AxisCare — ID ${res.axisId}${res.skipped.length ? ` (not accepted: ${res.skipped.join(', ')})` : ''}`
        : `✓ Linked to existing AxisCare client — ID ${res.axisId}`);
    } catch (err) {
      setAxisCareMsg(`⚠ ${err.message}`);
    } finally {
      setAxisCareBusy(false);
      setTimeout(() => setAxisCareMsg(''), 8000);
    }
  };

  if (loading) return <div className="empty-state"><div className="loading-spinner" style={{ margin:'0 auto' }} /></div>;
  if (!client) return <div className="empty-state"><p>Client not found.</p></div>;

  const getAge = (dob) => {
    if (!dob) return null;
    const birthDate = parseLocalDate(dob);
    const years = differenceInYears(new Date(), birthDate);
    if (years === 0) {
      const months = differenceInMonths(new Date(), birthDate);
      return `${months}mo`;
    }
    return `${years}y`;
  };

  const age = getAge(client.date_of_birth);
  const isPrivateClient = isPrivateTrack(client);
  const suppliesOnly = isDmeOnly(client);
  const openAssessment = async () => { navigate(await resolveAssessmentPath(client)); };

  // Single ordered tab list — first five are the primary workflow tabs, then the
  // operational tabs in a logical order. ProfileTabBar shows as many as fit and
  // puts the rest under "More ▾" (fixed 4 on mobile). Tasks carries an open-count badge.
  const openTaskCount = tasks.filter(t => t.status !== 'Done').length;
  const canVerify = getAccess('scheduling');
  // Order agreed 2026-09-27: the record first (Overview, Clinical,
  // Documents), then care delivery (Calendar, Schedule, Visit Verification,
  // and DME for clients who get supplies), then the log, the work and QM.
  // Grouping keeps the bar short enough that nothing important hides under
  // "More".
  const PROFILE_TABS = [
    { key: 'overview',     label: 'Overview' },
    { key: 'clinical',     label: 'Clinical' },
    { key: 'documents',    label: 'Documents' },
    { key: 'calendar',     label: 'Calendar' },
    { key: 'schedule',     label: 'Schedule' },
    ...(canVerify ? [{ key: 'verification', label: 'Visit Verification' }] : []),
    ...(hasDme(client) ? [{ key: 'dme', label: 'DME' }] : []),
    { key: 'activity',     label: 'Activity Log' },
    { key: 'tasks',        label: 'Tasks', badge: openTaskCount },
    { key: 'qm',           label: 'Quality Mgmt' },
  ];
  const CLINICAL_SUBS = [
    ['summary',     'Summary'],
    ['assessments', 'Assessments'],
    ['careplan',    'Care Plan & ADLs'],
    ['supervision', 'Supervision'],
    ['mar',         'MAR'],
    ['reports',     'Caregiver Reports'],
  ];
  // Quality Mgmt, this client's slice of it.
  const QM_SUBS = [
    ['surveys',    'Surveys'],
    ['complaints', 'Complaint Log'],
    ['incidents',  'Incident Log'],
    ['reports',    'Reports'],
  ];
  // Documents: the paperwork jobs, named for what staff do. A client who is
  // not on GAPP has no DCH submissions; their documents start at e-Signatures.
  const DOCUMENT_SUBS = [
    ['files', 'Files'],
    ...(isGapp(client) ? [['submissions', 'GAPP Submissions']] : []),
    ['esign', 'e-Signatures'],
    ['faxes', 'Faxes'],
    ['docreview', 'Document Review'],
  ];
  const SUBS = { clinical: CLINICAL_SUBS, qm: QM_SUBS, documents: DOCUMENT_SUBS };
  const docSub = activeTab === 'documents' ? (DOCUMENT_SUBS.some(([k]) => k === sub) ? sub : DOCUMENT_SUBS[0][0]) : null;
  return (
    <div>
      {/* The trail stays at the top as the page scrolls, and the tabs stay
          just under it — the way back to the client and to any tab is always
          one click, however far down a long tab you are. */}
      <ClientTrail client={client} crumbs={tabCrumbs(client.id, activeTab, activeTab === 'documents' ? docSub : SUBS[activeTab] ? sub : null)}
        right={<RecordNav {...recordNav} label="client" />} />

      {/* Header */}
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ padding:24, display:'flex', alignItems:'center', gap:20, flexWrap:'wrap' }}>
          <div style={{ width:56, height:56, borderRadius:'50%', background:'var(--navy)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:600, color:'#fff', flexShrink:0 }}>
            {client.first_name?.[0]}{client.last_name?.[0]}
          </div>
          <div style={{ flex:1, minWidth:200 }}>
            <div style={{ fontSize:20, fontWeight:600, color:'var(--text)' }}>
              {client.last_name}, {client.first_name}{client.middle_name ? ` ${client.middle_name}` : ''}{client.suffix ? ` ${client.suffix}` : ''}
              {client.goes_by && <span style={{ fontSize:14, fontWeight:400, color:'var(--text3)' }}> "{client.goes_by}"</span>}
            </div>
            <div style={{ fontSize:12, color:'var(--text3)', marginTop:3 }}>
              {client.client_id} · {client.gender}
              {client.date_of_birth && (
                <> · DOB: {format(parseLocalDate(client.date_of_birth), 'MMM d, yyyy')}
                  {age && <span style={{ marginLeft:6, background:'var(--accent-bg)', color:'var(--accent)', borderRadius:10, padding:'1px 7px', fontSize:11, fontWeight:500 }}>{age}</span>}
                </>
              )}
              {client.medicaid_id && <> · <MedicaidChip id={client.medicaid_id} /></>}
              {client.county && <> · {client.county} County</>}
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <span className={`badge ${zoneBadge(client.current_status)}`} style={{ fontSize:12, padding:'5px 12px' }}>{client.current_status}</span>
            {/* Household pets, for placing the right nurse. "Not asked" until the family answers. */}
            <span className="badge" title={client.household_pets_notes || undefined}
              style={{ fontSize:11, padding:'4px 10px', background:'var(--bg2)', color: hasPets(client) ? 'var(--text)' : 'var(--text3)', border:'1px solid var(--border)' }}>
              🐾 {petSummary(client)}{client.household_pets_notes ? ' · note' : ''}
            </span>
            {!client.nurse_assessment_complete && !suppliesOnly && !isClosedClient(client) && (
              <span className="badge badge-urgent" style={{ fontSize:11 }}>Nurse assessment pending</span>
            )}
            {client.mfmc_status === 'yes' && (
              <span title="Medically Frail / Compromised — requires RN/LPN supervision of personal care and on-call nurse access (Rule 111-8-65-.10)"
                style={{ fontSize:11, fontWeight:700, background:'var(--danger-bg)', color:'var(--danger)', border:'1px solid var(--danger-border)', borderRadius:10, padding:'3px 9px' }}>
                ⚕ MF/MC
              </span>
            )}
            {isReceivingService(client) && !client.axiscare_client_id && (
              <button className="btn btn-outline btn-sm" onClick={createClientInAxisCare} disabled={axisCareBusy}>
                {axisCareBusy ? <span className="loading-spinner" /> : null} Create in AxisCare
              </button>
            )}
            <button className="btn btn-outline btn-sm" onClick={() => navigate(`/clients/${id}/edit`)}>Edit</button>
            <button className="btn btn-accent btn-sm" onClick={() => setShowStageModal(true)}>Move Stage →</button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowLogModal(true)}>+ Log Activity</button>
          </div>
          {axisCareMsg && (
            <div style={{ width:'100%', fontSize:12, marginTop:8, color: axisCareMsg.startsWith('✓') ? 'var(--success)' : 'var(--danger)' }}>
              {axisCareMsg}
            </div>
          )}
        </div>
      </div>

      {/* Tabs — dynamic single-row bar with overflow under "More ▾" */}
      <div className="client-tabs-pinned" style={{ position: 'sticky', zIndex: 25, background: 'var(--bg)' }}>
      <ProfileTabBar
        tabs={PROFILE_TABS}
        activeKey={activeTab}
        onSelect={setActiveTab}
        mobileVisible={4}
      />

      {SUBS[activeTab] && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', padding: '10px 0 14px' }}>
          {SUBS[activeTab].map(([k, label]) => (
            <button key={k} onClick={() => setTabs(activeTab, k)}
              style={{ padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                border: '0.5px solid', background: sub === k ? 'var(--accent)' : 'transparent',
                color: sub === k ? 'var(--white)' : 'var(--text3)', borderColor: sub === k ? 'var(--accent)' : 'var(--border)' }}>{label}</button>
          ))}
        </div>
      )}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
        <div className="grid-2">
          {/* LEFT: Contact & Case */}
          <div>
            <Section title="Primary Contact">
              <Detail label="Address" value={[client.address, client.city, client.state, client.zip].filter(Boolean).join(', ')} />
              <Detail label="County" value={client.county} />
              <Detail label="Language" value={client.language} />
              <Detail label="Guardian" value={`${client.guardian_last_name ? `${client.guardian_last_name}, ${client.guardian_first_name || ''}`.trim() : (client.guardian_first_name || '')}${client.guardian_relationship ? ` (${client.guardian_relationship})` : ''}`} />
              <Detail label="Phone" value={client.guardian_phone} />
              <Detail label="Email" value={client.guardian_email} />
              <Detail label="Address" value={client.guardian_address} />
            </Section>
            <Section title="Emergency Contact">
              <Detail label="Name" value={`${client.emergency_last_name ? `${client.emergency_last_name}, ${client.emergency_first_name || ''}`.trim() : (client.emergency_first_name || '')}${client.emergency_relationship ? ` (${client.emergency_relationship})` : ''}`} />
              <Detail label="Phone" value={client.emergency_phone} />
              <Detail label="Email" value={client.emergency_email} />
              <Detail label="Address" value={client.emergency_address} />
            </Section>
            <Section title="Referral & Assignment">
              <Detail label="Referral Source" value={client.referral_source} />
              <Detail label="Referral Date" value={client.referral_date ? format(parseLocalDate(client.referral_date),'MMM d, yyyy') : '—'} />
              <Detail label="Responsible Person" value={client.responsible_person_name} />
              <Detail label="Service Type" value={
                payerLabel(client.service_type)
              } />
              {/* Worked out from the county and Settings → Approved Counties —
                  never a stored Yes/No (retired 2026-10-20). Staff-only page. */}
              <Detail label="Service Area" value={(() => {
                if (!approvedList) return '—';
                const st = serviceAreaStatus(client.county, approvedList);
                if (st === 'none') return 'No county recorded';
                if (st === 'unrecognised') return <span style={{ color:'var(--warn)' }}>{client.county} · county not recognised</span>;
                return st === 'approved'
                  ? <span>{client.county} · <span style={{ color:'var(--success)' }}>approved area</span></span>
                  : <span>{client.county} · <span style={{ color:'var(--warn)' }}>outside approved area</span></span>;
              })()} />
            </Section>
          </div>
          {/* RIGHT: Authorization & Funding */}
          <div>
            <Section title="Coverage">
              <CoverageCard client={client} history={eligHistory} />
            </Section>
            <Section title="Insurance & Funding">
              {/* The Medicaid ID sits in the header, on every tab, since 2026-09-20. */}
              <Detail label="Private Insurance" value={client.private_insurance ? `Yes — ${client.insurance_name || ''}` : 'No'} />
            </Section>
            <Section title="School & DFCS">
              <Detail label="In School"           value={client.in_school === true ? 'Yes' : client.in_school === false ? 'No' : null} />
              <Detail label="Hours/Day"            value={client.school_hours_per_day} />
              <Detail label="Personal Nurse"       value={client.personal_nurse_in_school === true ? 'Yes' : client.personal_nurse_in_school === false ? 'No' : null} />
              <Detail label="Has IEP"              value={client.has_iep === true ? 'Yes' : client.has_iep === false ? 'No' : null} />
              <Detail label="IEP Provided"         value={client.iep_provided === true ? 'Yes' : client.iep_provided === false ? 'No' : null} />
              <Detail label="In DFCS Custody"      value={client.in_dfcs_custody === true ? 'Yes' : client.in_dfcs_custody === false ? 'No' : null} />
              <Detail label="DFCS Case Manager"    value={client.dfcs_case_manager} />
              <Detail label="GAPP Household Member" value={client.gapp_household_member === true ? 'Yes' : client.gapp_household_member === false ? 'No' : null} />
            </Section>
          </div>
        </div>

        {/* General Notes — full width */}
        <div className="card" style={{ marginTop:12, border:'0.5px solid var(--accent-border)' }}>
          <div style={{ padding:'10px 16px', borderBottom:'0.5px solid var(--accent-border)', background:'var(--accent-bg)' }}>
            <div style={{ fontSize:11, fontWeight:600, color:'var(--accent)', textTransform:'uppercase', letterSpacing:'0.07em' }}>General Notes</div>
          </div>
          <div style={{ padding:'12px 16px' }}>
            {client.general_notes ? (
              <p style={{ fontSize:13, color:'var(--text)', lineHeight:1.6, whiteSpace:'pre-wrap' }}>{client.general_notes}</p>
            ) : (
              <p style={{ fontSize:13, color:'var(--text3)', fontStyle:'italic' }}>No general notes added yet.</p>
            )}
          </div>
        </div>

        {/* Prior Authorization — full width */}
        <div className="card" style={{ marginTop:12, border:'0.5px solid var(--accent-border)' }}>
          <div style={{ padding:'10px 16px', borderBottom:'0.5px solid var(--accent-border)', background:'var(--accent-bg)' }}>
            <div style={{ fontSize:11, fontWeight:600, color:'var(--accent)', textTransform:'uppercase', letterSpacing:'0.07em' }}>Prior Authorization</div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1px 1fr' }}>
            {/* DMA 6A */}
            <div style={{ padding:'12px 16px' }}>
              <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--accent)', marginBottom:10, paddingBottom:4, borderBottom:'0.5px solid var(--accent-border)' }}>DMA 6A</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:16 }}>
                <div><div style={{ fontSize:10, color:'var(--text3)', marginBottom:3 }}>Number</div><div style={{ fontSize:12, fontWeight:500, fontFamily:'monospace', color:'var(--text)' }}>{client.dma_6a_number || '—'}</div></div>
                <div><div style={{ fontSize:10, color:'var(--text3)', marginBottom:3 }}>Submitted</div><div style={{ fontSize:12, fontWeight:500, color:'var(--text)' }}>{client.dma_6a_submission_date ? format(parseLocalDate(client.dma_6a_submission_date),'MMM d, yyyy') : '—'}</div></div>
                <div><div style={{ fontSize:10, color:'var(--text3)', marginBottom:3 }}>Expiry</div><div style={{ fontSize:12, fontWeight:500, color:'var(--text)' }}>{client.dma_6a_expiry_date ? dateVal(client.dma_6a_expiry_date) : '—'}</div></div>
              </div>
            </div>
            <div style={{ background:'var(--accent-border)' }} />
            {/* DMA 80 / PA */}
            <div style={{ padding:'12px 16px' }}>
              <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--accent)', marginBottom:10, paddingBottom:4, borderBottom:'0.5px solid var(--accent-border)' }}>DMA 80 / PA</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:16 }}>
                <div><div style={{ fontSize:10, color:'var(--text3)', marginBottom:3 }}>Number</div><div style={{ fontSize:12, fontWeight:500, fontFamily:'monospace', color:'var(--text)' }}>{client.dma_80_pa_number || '—'}</div></div>
                <div><div style={{ fontSize:10, color:'var(--text3)', marginBottom:3 }}>Submitted</div><div style={{ fontSize:12, fontWeight:500, color:'var(--text)' }}>{client.dma_80_submission_date ? format(parseLocalDate(client.dma_80_submission_date),'MMM d, yyyy') : '—'}</div></div>
                <div><div style={{ fontSize:10, color:'var(--text3)', marginBottom:3 }}>PA Expiry</div><div style={{ fontSize:12, fontWeight:500, color:'var(--text)' }}>{client.pa_expiry_date ? dateVal(client.pa_expiry_date) : '—'}</div></div>
                <div><div style={{ fontSize:10, color:'var(--text3)', marginBottom:3 }}>PA Renewal</div><div style={{ fontSize:12, fontWeight:500, color:'var(--text)' }}>{client.pa_renewal_date ? dateVal(client.pa_renewal_date) : '—'}</div></div>
                <div><div style={{ fontSize:10, color:'var(--text3)', marginBottom:3 }}>Skilled Hrs/Wk</div><div style={{ fontSize:12, fontWeight:500, color:'var(--text)' }}>{client.pa_skilled_hours_week != null ? client.pa_skilled_hours_week : '—'}</div></div>
                <div><div style={{ fontSize:10, color:'var(--text3)', marginBottom:3 }}>PSS Hrs/Wk</div><div style={{ fontSize:12, fontWeight:500, color:'var(--text)' }}>{client.pa_pss_hours_week != null ? client.pa_pss_hours_week : '—'}</div></div>
              </div>
            </div>
          </div>
          {/* Authorization history — merged into the Prior Authorization card */}
          <div style={{ borderTop:'0.5px solid var(--accent-border)', padding:'10px 16px 6px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
              <div style={{ fontSize:11, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.07em' }}>Authorization History</div>
              {authHistory?.length ? <span style={{ fontSize:11, color:'var(--text3)' }}>{authHistory.length} record{authHistory.length !== 1 ? 's' : ''}</span> : null}
            </div>
            <AuthHistoryTable rows={authHistory} showType />
          </div>
        </div>
        </>
      )}

      {/* Clinical ▸ Summary — this client's condition, as assessed */}
      {activeTab === 'clinical' && sub === 'summary' && (
        <div>
          {/* No assessment banner of either kind for supplies-only or closed clients. */}
          {suppliesOnly || isClosedClient(client) ? null : !client.nurse_assessment_complete ? (
            <div style={{ background:'var(--warn-bg)', border:'1px solid var(--warn-border)', borderRadius:10, padding:'14px 18px', marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
              <svg width="18" height="18" fill="none" stroke="var(--warn)" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <span style={{ fontSize:13, color:'var(--warn)' }}>{isPrivateClient
                ? 'Intake assessment not yet completed. It is filled in the client’s Private Home Care Intake packet.'
                : 'Nurse assessment not yet completed. Fill in the clinical fields below after the home visit.'}</span>
              <button className="btn btn-sm" style={{ background:'var(--warn)', color:'#fff', marginLeft:'auto' }} onClick={openAssessment}>Complete Assessment</button>
            </div>
          ) : (
            <div style={{ background:'var(--success-bg)', border:'1px solid var(--success-border)', borderRadius:10, padding:'14px 18px', marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
              <svg width="18" height="18" fill="none" stroke="var(--success)" strokeWidth="2" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>
              <span style={{ fontSize:13, color:'var(--success)', fontWeight:600 }}>Assessment complete{client.nurse_assessment_date ? ` — ${client.nurse_assessment_date}` : ''}.</span>
              <button className="btn btn-sm" style={{ background:'none', color:'var(--text)', border:'1.5px solid var(--border2)', marginLeft:'auto' }} onClick={openAssessment}>Review / Update</button>
            </div>
          )}

          {/* Supervisor visit banner — only for Active (Receiving Service) clients.
              Shows the most urgent supervision item for this client and sends
              every button to the client's own Supervision tab, which is where
              visits are scheduled and recorded since Supervision v2. The old
              buttons pointed at /hh/supervisor-visits, a route that no longer
              exists, so all four of them landed somewhere else. */}
          {isReceivingService(client) && (() => {
            const RANK = { overdue: 0, due: 1, ok: 2 };
            const rows = [...supervision].sort((x, y) =>
              (RANK[x.status] ?? 3) - (RANK[y.status] ?? 3) || String(x.next_due || '').localeCompare(String(y.next_due || '')));
            const top = rows[0];
            const fmtD = (d) => (d ? format(new Date(`${d}T12:00:00`), 'MMM d, yyyy') : '');
            const who = (r) => r.kind === 'aide'
              ? `${r.staff_full || 'caregiver'}${r.position_code ? ` (${r.position_code})` : ''}`
              : `${client.first_name}'s RN visit`;
            const others = rows.filter((r) => r !== top && r.status !== 'ok').length;
            const more = others ? ` · ${others} more due` : '';

            let tone, icon, msg, btnLabel;
            if (!top) {
              tone = 'warn'; icon = <circle cx="12" cy="12" r="10"/>;
              msg = 'No supervision set up for this client yet'; btnLabel = 'Open supervision';
            } else if (top.status === 'overdue') {
              tone = 'danger';
              icon = <><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/></>;
              msg = `Supervision overdue — ${who(top)}, was due ${fmtD(top.next_due)}${more}`; btnLabel = 'Open supervision';
            } else if (top.status === 'due') {
              tone = 'warn';
              icon = <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>;
              msg = `Supervision due — ${who(top)}${top.next_due ? `, ${fmtD(top.next_due)}` : ''}${more}`; btnLabel = 'Open supervision';
            } else {
              tone = 'success'; icon = <path d="M20 6L9 17l-5-5"/>;
              msg = top.next_due ? `Next supervisor visit due ${fmtD(top.next_due)}` : 'Supervision up to date'; btnLabel = 'View visits';
            }
            const c = { danger: 'var(--danger)', warn: 'var(--warn)', success: 'var(--success)' }[tone];
            return (
              <div style={{ background:`var(--${tone}-bg)`, border:`1px solid var(--${tone}-border)`, borderRadius:10, padding:'14px 18px', marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
                <svg width="18" height="18" fill="none" stroke={c} strokeWidth="2" viewBox="0 0 24 24">{icon}</svg>
                <span style={{ fontSize:13, color:c, flex:1 }}>{msg}</span>
                <button className="btn btn-sm" style={{ background:c, color:'var(--white)', marginLeft:'auto', whiteSpace:'nowrap' }}
                  onClick={() => setTabs('clinical', 'supervision')}>{btnLabel}</button>
              </div>
            );
          })()}

          {/* Row 1: Clinical Overview — merged diagnosis/vitals/flags, full width */}
          <div className="card" style={{ marginBottom:12, border:'0.5px solid var(--accent-border)' }}>
            <div style={{ padding:'10px 16px', borderBottom:'0.5px solid var(--accent-border)', background:'var(--accent-bg)' }}>
              <div style={{ fontSize:11, fontWeight:600, color:'var(--accent)', textTransform:'uppercase', letterSpacing:'0.07em' }}>Clinical Overview</div>
            </div>

            <div style={{ padding:'14px 16px', borderBottom:'0.5px solid var(--border)' }}>
              <div style={{ fontSize:10, color:'var(--accent)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Summary</div>
              <div style={{ fontSize:13, color:'var(--text)', lineHeight:1.6 }}>{client.diagnosis_summary || '—'}</div>
              <div style={{ fontSize:10, color:'var(--accent)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', margin:'10px 0 6px' }}>Full Diagnoses</div>
              <div style={{ fontSize:13, color:'var(--text)', lineHeight:1.6 }}>{client.full_diagnoses || '—'}</div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)' }}>
              <GridCell label="Diagnosis Code(s)" value={client.diagnosis_code} mono />
              <GridCell label="Weight" value={client.weight_lbs ? `${client.weight_lbs} lbs` : null} />
              <GridCell label="Diet" value={client.diet_description} />
              <GridCell label="Allergies" value={client.has_allergies ? client.allergies : 'None'} />
              <GridCell label="Medications" value={client.current_medications} span={2} />
              <GridCell label="Hospitalizations" value={client.hospitalizations} span={2} />
            </div>

            <div style={{ padding:'12px 14px' }}>
              <div style={{ fontSize:10, color:'var(--accent)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Medical Flags</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:8 }}>
                <FlagChip label="Seizures"          value={client.seizures} />
                <FlagChip label="G-Tube"            value={client.g_tube} />
                <FlagChip label="Uses Oxygen"       value={client.uses_oxygen} />
                <FlagChip label="Potty Trained"     value={client.potty_trained} />
                <FlagChip label="Wears Diapers"     value={client.diapers} />
                <FlagChip label="Contractures"      value={client.contractures} />
                <FlagChip label="Swallowing Issues" value={client.swallowing_issues} />
                <FlagChip label="Has Allergies"     value={client.has_allergies} />
              </div>
            </div>
          </div>

          {/* Row 2: Equipment, Therapies & Abilities — full width */}
          <div className="card" style={{ marginBottom:12, border:'0.5px solid var(--accent-border)' }}>
            <div style={{ padding:'10px 16px', borderBottom:'0.5px solid var(--accent-border)', background:'var(--accent-bg)' }}>
              <div style={{ fontSize:11, fontWeight:600, color:'var(--accent)', textTransform:'uppercase', letterSpacing:'0.07em' }}>Equipment, Therapies & Abilities</div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:20, padding:'14px 16px', borderBottom:'0.5px solid var(--border)' }}>
              <div>
                <div style={{ fontSize:10, color:'var(--accent)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Equipment</div>
                <div>{client.medical_equipment?.length > 0 ? client.medical_equipment.map(e => <Tag key={e}>{e}</Tag>) : <span style={{ fontSize:12, color:'var(--text3)' }}>None</span>}</div>
              </div>
              <div>
                <div style={{ fontSize:10, color:'var(--accent)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Therapies</div>
                <div>{client.therapies?.length > 0 ? client.therapies.map(t => <Tag key={t}>{t}</Tag>) : <span style={{ fontSize:12, color:'var(--text3)' }}>None</span>}</div>
              </div>
              <div>
                <div style={{ fontSize:10, color:'var(--accent)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Assistance Needed</div>
                <div>{client.assistance_needed?.length > 0 ? client.assistance_needed.map(a => <Tag key={a}>{a}</Tag>) : <span style={{ fontSize:12, color:'var(--text3)' }}>None</span>}</div>
              </div>
            </div>
            <div style={{ padding:'10px 16px' }}>
              <div style={{ fontSize:10, color:'var(--accent)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Abilities</div>
              <div>{client.abilities?.length > 0 ? client.abilities.map(a => <Tag key={a}>{a}</Tag>) : <span style={{ fontSize:12, color:'var(--text3)' }}>None recorded</span>}</div>
            </div>
          </div>

          {/* Row 3: Care team — from the Provider Directory */}
          <ClientCareTeam client={client} onChanged={loadClient} />
        </div>
      )}

      {/* Activity Log Tab */}
      {activeTab === 'activity' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Activity Log</div>
            <button className="btn btn-primary btn-sm" onClick={() => setShowLogModal(true)}>+ Log Entry</button>
          </div>
          {activityLog.length === 0 ? (
            <div className="empty-state"><p>No activity logged yet.</p></div>
          ) : (
            <div style={{ padding:'0 20px' }}>
              {activityLog.map((item, i) => (
                <div key={item.id} style={{ display:'flex', gap:16, padding:'14px 0', borderBottom: i < activityLog.length-1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                    <div style={{ width:10, height:10, borderRadius:'50%', background: item.new_status ? 'var(--accent)' : 'var(--text3)', marginTop:3, flexShrink:0 }} />
                    {i < activityLog.length-1 && <div style={{ width:1, flex:1, background:'var(--border)' }} />}
                  </div>
                  <div style={{ flex:1, paddingBottom:4 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                      <span style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>{item.new_status || item.activity_type}</span>
                      {item.previous_status && <span style={{ fontSize:11, color:'var(--text3)' }}>from {item.previous_status}</span>}
                    </div>
                    <ActivityNote item={item} onSaved={() => getActivityLog(id).then(({ data: logs }) => setActivityLog(logs || []))} />
                    <div style={{ fontSize:11, color:'var(--text3)' }}>
                      {item.performed_by_name} · {format(new Date(item.created_at), 'MMM d, yyyy h:mm a')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tasks Tab */}
      {activeTab === 'tasks' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Tasks for {client.first_name}</div>
            <button className="btn btn-primary btn-sm" onClick={() => { setTaskError(''); setShowTaskModal(true); }}>+ Add Task</button>
          </div>
          {tasks.length === 0 ? (
            <div className="empty-state"><p>No tasks assigned to this client.</p></div>
          ) : (
            <div style={{ padding:'0 20px' }}>
              {tasks.map(task => (
                <div key={task.id} style={{ display:'flex', gap:12, padding:'12px 0', borderBottom:'1px solid var(--border)', alignItems:'flex-start' }}>
                  <div style={{ width:18, height:18, borderRadius:4, border:`2px solid ${task.status==='Done'?'var(--success)':'var(--border)'}`, background:task.status==='Done'?'var(--success)':'transparent', flexShrink:0, marginTop:2 }} />
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ fontSize:13, fontWeight:500, color:'var(--text)', textDecoration:task.status==='Done'?'line-through':'' }}>{task.title}</span>
                      <span className={`badge badge-${task.priority?.toLowerCase()}`}>{task.priority}</span>
                    </div>
                    <div style={{ fontSize:11, color:'var(--text3)', marginTop:3, marginBottom:4 }}>
                      {task.assigned_to_name && `Assigned to ${task.assigned_to_name}`}
                      {task.due_date && ` · Due ${format(parseLocalDate(task.due_date),'MMM d, yyyy')}`}
                    </div>
                    <InlineNote
                      value={task.notes}
                      size={12}
                      placeholder="No note"
                      canEdit={canEditNotes(profile?.role)}
                      onSave={async (text) => {
                        const { error } = await updateTask(task.id, { notes: text || null });
                        if (error) throw new Error(error.message);
                        const { data } = await getTasks({ clientId: id });
                        setTasks(data || []);
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Documents — GAPP Submissions · e-Signatures · Faxes · Files */}
      {activeTab === 'documents' && docSub === 'submissions' && (
        <ClientSubmissions client={client} refreshKey={faxRefresh}
          onRequestProvider={() => openRequest()}
          onUpload={(bucket) => { setUploadForm(p => ({ ...p, bucket: bucket || 'Needs Review' })); setUploadLocked(!!bucket); setShowDocModal(true); }}
          onOpenSub={(k) => setTabs('documents', k)}
          onMoveStage={({ status, notes, familyEmailHandled = false }) => { setStagePrefill({ status, notes, familyEmailHandled }); setShowStageModal(true); }} />
      )}
      {activeTab === 'documents' && docSub === 'esign' && <ClientESignatures client={client} refreshKey={faxRefresh} />}
      {activeTab === 'documents' && docSub === 'faxes' && <ClientFaxesTab client={client} onRequest={openRequest} refreshKey={faxRefresh} />}
      {activeTab === 'documents' && docSub === 'docreview' && <ClientDocReviewTab client={client} profile={profile} />}
      {activeTab === 'documents' && docSub === 'files' && (
        <ClientDocumentsTab client={client} driveTree={driveTree} driveLoading={driveLoading} driveErr={driveErr}
          onRefresh={() => loadDrive(client.client_id, client.id, client.drive_folder_id)} documents={documents} latestAssessment={latestAssessment}
          onUpload={(bucket) => { setUploadForm(p => ({ ...p, bucket: bucket || 'Needs Review' })); setUploadLocked(!!bucket); setShowDocModal(true); }} />
      )}

      {/* Per-client operational tabs */}
      {activeTab === 'calendar'     && <ClientVisitsView client={client} />}
      {activeTab === 'schedule'     && <ClientScheduleTab client={client} />}
      {activeTab === 'verification' && canVerify && <ClientVerificationTab client={client} />}
      {activeTab === 'clinical' && sub === 'assessments' && <ClientAssessments client={client} />}
      {activeTab === 'clinical' && sub === 'careplan'    && <ClientCarePlan client={client} />}
      {activeTab === 'clinical' && sub === 'supervision'  && <ClientSupervisorVisitsTab client={client} />}
      {activeTab === 'clinical' && sub === 'mar'          && <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}><MarPanel client={client} /></div>}
      {activeTab === 'clinical' && sub === 'reports'      && <CaregiverReports client={client} />}
      {activeTab === 'dme'          && <ClientDmeTab client={client} />}
      {activeTab === 'qm'           && <ClientQmTab client={client} view={sub === 'summary' ? 'surveys' : sub} />}


      {/* Add Task Modal — top level so it works from any tab */}
      {showTaskModal && (
        <div className="modal-overlay" onMouseDown={e => e.target===e.currentTarget && setShowTaskModal(false)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Add Task for {client.first_name}</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowTaskModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Task Title <span className="req">*</span></label>
                <input className="form-input" value={taskForm.title} onChange={e => setTaskForm(p=>({...p,title:e.target.value}))} placeholder="What needs to be done?" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Assign To</label>
                  <select className="form-select" value={taskForm.assigned_to_id} onChange={e => setTaskForm(p=>({...p,assigned_to_id:e.target.value}))}>
                    <option value="">Unassigned</option>
                    {taskStaff.map(s => <option key={s.id} value={s.id}>{fullNameLastFirst(s.full_name)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select className="form-select" value={taskForm.priority} onChange={e => setTaskForm(p=>({...p,priority:e.target.value}))}>
                    <option>Urgent</option><option>Normal</option><option>Low</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Due Date</label>
                <input type="date" className="form-input" value={taskForm.due_date} onChange={e => setTaskForm(p=>({...p,due_date:e.target.value}))} />
              </div>
            </div>
            {taskError && (
              <div style={{ margin:'0 20px 12px', padding:'10px 14px', background:'var(--danger-bg)', border:'1px solid var(--danger-border)', borderRadius:6, fontSize:13, color:'var(--danger)' }}>
                {taskError}
              </div>
            )}
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowTaskModal(false)} disabled={taskSaving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreateTask} disabled={taskSaving}>
                {taskSaving ? 'Saving...' : 'Create Task'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attach File Modal — top level */}
      {showRequestModal && (
        <PhysicianRequestModal key={requestDraft?.id || 'new'} client={client} profile={profile} draft={requestDraft}
          onClose={() => { setShowRequestModal(false); setRequestDraft(null); setFaxRefresh(n => n + 1); }}
          driveFiles={driveTree ? [...(driveTree.folders || []).flatMap(b => flattenBucket(b).map(f => ({ ...f, bucket: b.name }))), ...(driveTree.files || []).map(f => ({ ...f, bucket: '' }))] : []}
          onFiled={(r) => {
            if (!client.drive_folder_id && r?.clientFolderId) setClient(c => ({ ...c, drive_folder_id: r.clientFolderId }));
            getDocuments(id).then(({ data: d }) => setDocuments(d || []));
            loadDrive(client.client_id, client.id, client.drive_folder_id);
          }}
          onSent={() => { setFaxRefresh(n => n + 1); loadDrive(client.client_id, client.id, client.drive_folder_id); }} />
      )}

      {showDocModal && (
        <div className="modal-overlay" onMouseDown={e => { if (e.target===e.currentTarget) { setShowDocModal(false); setUploadLocked(false); } }}>
          <div className="modal" onMouseDown={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{uploadLocked ? `Upload to ${uploadForm.bucket}` : 'Upload a file'}</div>
              <button className="btn btn-ghost btn-sm" onClick={() => { setShowDocModal(false); setUploadLocked(false); }}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ background:'var(--accent-bg)', border:'1px solid var(--accent-border)', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12, color:'var(--accent)' }}>
                For anything that didn't come from an assessment, packet, or fax — a scanned form, something a doctor emailed. A copy goes into {uploadLocked ? <b>{uploadForm.bucket}</b> : 'the folder you pick'} in {client.first_name}'s Drive folder and shows there with your name on it; the original stays on your computer.
              </div>
              <div className="form-group">
                <label className="form-label">File <span className="req">*</span></label>
                <input className="form-input" type="file" onChange={e => setUploadForm(p => ({ ...p, file: e.target.files?.[0] || null }))} />
              </div>
              {!uploadLocked && (
                <div className="form-group">
                  <label className="form-label">File goes in</label>
                  <select className="form-select" value={uploadForm.bucket} onChange={e => setUploadForm(p => ({ ...p, bucket: e.target.value }))}>
                    {uploadTargets(driveTree).map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" rows={2} value={uploadForm.notes} onChange={e => setUploadForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes about this document..." />
              </div>
              {uploadError && <div style={{ color:'var(--danger)', fontSize:12.5, marginTop:4 }}>{uploadError}</div>}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => { setShowDocModal(false); setUploadLocked(false); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAttachFile} disabled={docSaving || !uploadForm.file}>
                {docSaving ? <span className="loading-spinner" style={{ borderTopColor:'#fff' }} /> : null}
                {docSaving ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stage Transition Modal */}
      {showStageModal && (
        <StageTransitionModal
          client={client}
          initialStatus={stagePrefill.status}
          initialNotes={stagePrefill.notes}
          familyEmailHandled={stagePrefill.familyEmailHandled}
          onClose={() => { setShowStageModal(false); setStagePrefill({ status: '', notes: '', familyEmailHandled: false }); }}
          onSuccess={(newStatus) => {
            setClient(prev => ({ ...prev, current_status: newStatus }));
            // Moving to Active may have created them in AxisCare.
            getClient(id).then(({ data: fresh }) => fresh && setClient(prev => ({ ...prev, axiscare_client_id: fresh.axiscare_client_id })));
            getActivityLog(id).then(({ data: logs }) => setActivityLog(logs || []));
          }}
        />
      )}

      {/* Log Activity Modal */}
      {showLogModal && (
        <div className="modal-overlay" onMouseDown={e => e.target===e.currentTarget && setShowLogModal(false)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Log Activity</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowLogModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Activity Type</label>
                <select className="form-select" value={logType} onChange={e => setLogType(e.target.value)}>
                  {['Referral Received','Initial Outreach','Phone Call — No Answer','Phone Call — Reached','Email Sent','Client Documentation Followup','Family Meeting','GAMMIS Eligibility Check','In-Home Nurse Assessment','Clinical Review','Doctor Signature Requested','Doctor Signature Received','Medical Records Request','GAPP Packet Submitted','GAMMIS Follow-up','LON Received','Care Coordination','Insurance Verification','Approval Received','GAPP Application Denied','Authorisation Renewal','Case Activation','Service Suspended','Incident Report Filed','Complaint Received','Discharge/Closed','Pending County Approval','60-Day Nurse/Supervisory Visit','General Note'].map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Notes <span className="req">*</span></label>
                <textarea className="form-textarea" value={logNote} onChange={e => setLogNote(e.target.value)} placeholder="What happened? What was done?" rows={4} />
              </div>
              <div style={{ fontSize:12, color:'var(--text3)' }}>Logged by: {profile?.full_name} · {new Date().toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowLogModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleLogActivity}>Save Log Entry</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// The Medicaid ID in the header: the number typed into GAMMIS most, so one
// click copies it. Shown on every tab because the header is above the tabs.
function MedicaidChip({ id }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(id); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) { /* clipboard unavailable — the number is still on screen */ }
  };
  return (
    <button type="button" onClick={copy} title="Click to copy the Medicaid ID"
      style={{ font:'inherit', display:'inline-flex', alignItems:'center', gap:5, background:'var(--accent-bg)', color:'var(--accent)', border:'none', borderRadius:8, padding:'1px 7px', cursor:'pointer', fontSize:11.5, verticalAlign:'baseline' }}>
      <span style={{ fontSize:10, fontWeight:600, letterSpacing:'.04em', textTransform:'uppercase' }}>Medicaid</span>
      <span style={{ fontFamily:'"DM Mono", ui-monospace, monospace', fontWeight:500, letterSpacing:'.02em' }}>{id}</span>
      <span style={{ fontSize:10.5, opacity:.8 }}>{copied ? '✓ copied' : '⧉'}</span>
    </button>
  );
}
